import React, { useCallback, useMemo, useState } from 'react';
import {
  AddButton,
  ButtonGroup,
  ErrorHandler,
  Icon,
  PrimaryButton,
  TimeWithTimeZone,
  Link,
} from '@common/components';
import { useQuery, useNavigate, useLocation } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  Membership,
  WorksMembershipsQuery,
  WorksMembershipsResponse,
} from 'works/graphql/works-memberships';
import { FilteredTable } from 'works/components/filtered-table';
import FiltersStorage from 'works/legacy/lib/filters';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import {
  WorksMembershipRolesQuery,
  WorksMembershipRolesResponse,
} from 'works/graphql/works-membership-roles';
import {
  BuilderConfig,
  filterableColumnBuilder,
  normalizeSortingData,
} from 'works/legacy/lib/table-filters-helper';
import { Dropdown, TableColumnType } from 'antd';
import { MultiSelectTableFilter } from 'works/components/filtered-table/multi-select-table-filter';
import { SorterResult } from 'antd/lib/table/interface';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { SUPPLIER_ADMIN_MENU } from 'works/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu';
import { Colors } from 'works/legacy/lib/style-helper';
import { buildDialogUrl } from '@common/services';

const LOCALSTORAGE_FILTER_KEY = 'MembershipList';

function buildViewDrawerRoute(memberId: string) {
  return buildDialogUrl('user-view-drawer', {
    props: { userId: memberId },
  });
}

function renderName(
  isOrganization: boolean,
  value: string,
  member: { id: string; organizationUnitsCount: number; active: boolean },
  _index: number // eslint-disable-line @typescript-eslint/no-unused-vars
) {
  if (!member.active) {
    return <span style={{ color: Colors.midnightLighter }}>{value}</span>;
  }

  return (
    <Link
      className="members-list-row-link"
      to={buildViewDrawerRoute(member.id)}
    >
      {value}
    </Link>
  );
}

function getColumns(
  builderConfig: BuilderConfig<Membership>,
  isOrganization: boolean
): TableColumnType<Membership>[] {
  const column = filterableColumnBuilder(builderConfig);

  const columns = [
    column({
      key: 'firstName',
      title: 'First Name',
      priority: 1,
      filterDropdown: TextSearchTableFilter,
      width: 120,
      className: 'sentry-mask',
      render: (...args) => renderName(isOrganization, ...args),
    }),
    column({
      key: 'lastName',
      title: 'Last Name',
      priority: 2,
      filterDropdown: TextSearchTableFilter,
      width: 150,
      className: 'sentry-mask',
      render: (...args) => renderName(isOrganization, ...args),
    }),
    column({
      key: 'email',
      title: 'Email',
      priority: 3,
      width: 300,
      className: 'sentry-mask',
      filterDropdown: TextSearchTableFilter,
    }),
    column({
      key: 'role',
      dataIndex: 'roleName',
      title: 'Role',
      priority: 4,
      width: 180,
      filterDropdown: MultiSelectTableFilter,
    }),
    column({
      key: 'employeeId',
      dataIndex: 'employeeId',
      title: 'Employee ID',
      priority: 5,
      width: 180,
      filterDropdown: TextSearchTableFilter,
    }),
  ];

  const orgColumns = [
    column({
      key: 'organizationBusinessesCount',
      title: 'Facilities',
      priority: 6,
    }),
    column({
      key: 'organizationUnitsCount',
      title: 'Units',
      priority: 7,
    }),
    column({
      key: 'updatedAt',
      title: 'Last Updated',
      width: 208,
      priority: 8,
      render: (updatedAt: string) => (
        <TimeWithTimeZone date={updatedAt} variant="long_format" />
      ),
    }),
  ];

  return isOrganization ? columns.concat(orgColumns) : columns;
}

export const MembersList = ({
  entityType,
}: {
  entityType: 'organization' | 'supplier';
}) => {
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const { state } = useLocation<{ updatedMemberIds: string[] }>();
  const updatedMemberIds = state?.updatedMemberIds || [];
  const isOrganization = entityType === 'organization';

  const [selectedFilters, setSelectedFilters] = useState(
    FiltersStorage.getFilters(LOCALSTORAGE_FILTER_KEY)
  );

  const { data, loading, error, fetchMore } =
    useQuery<WorksMembershipsResponse>(WorksMembershipsQuery, {
      variables: {
        input: {
          filters: selectedFilters,
        },
      },
    });

  const membershipRoles = useQuery<WorksMembershipRolesResponse>(
    WorksMembershipRolesQuery
  );
  const memberships = data?.worksMemberships?.collection;
  const canUpdateAdminUsersUnits = currentMembership.canUpdateAdminUsersUnits;

  const showActionColumn = useMemo(
    () =>
      memberships?.some((membership) => !membership.organizationUnitsCount) &&
      isOrganization,
    [memberships, isOrganization]
  );

  const redirectToUpdateLocations = useCallback(
    (id?: string) => {
      navigate(`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`, {
        state: {
          memberIds: [id],
        },
      });
    },
    [navigate]
  );

  const tableColumnsWithFilters = useCallback(
    (isOrganization: boolean) => {
      const columns = getColumns(
        {
          paginated: true,
          filterConfig: {
            selectedFilters,
            filterOptions: {
              role: membershipRoles?.data?.worksMembershipRoles || [],
            },
          },
        },
        isOrganization
      );

      const actionColumn: TableColumnType<Membership> = {
        key: 'id',
        fixed: 'right',
        width: 152,
        render: (record: Membership) => {
          return record.organizationUnitsCount ? null : (
            <PrimaryButton
              className="members-list__action-button"
              onClick={() => redirectToUpdateLocations(record.id)}
              disabled={!record.canUserUpdateMembership}
            >
              Assign Locations
            </PrimaryButton>
          );
        },
      };

      if (showActionColumn) {
        columns.push(actionColumn);
      }

      return columns;
    },
    [
      selectedFilters,
      membershipRoles?.data?.worksMembershipRoles,
      redirectToUpdateLocations,
      showActionColumn,
    ]
  );

  function onFilterChange(newFilters: { [key: string]: unknown }) {
    setSelectedFilters(newFilters);
    FiltersStorage.setFilters(LOCALSTORAGE_FILTER_KEY, newFilters);
  }

  function handleTableChange(
    pagination: { current: number; pageSize: number },
    filters: object,
    sortingData: SorterResult<Membership>
  ) {
    fetchMore({
      variables: {
        input: {
          pagination: {
            page: pagination.current,
            pageSize: pagination.pageSize,
          },
          orders: {
            ...normalizeSortingData(sortingData),
          },
          filters,
        },
      },
      updateQuery(
        previousQueryResult,
        { fetchMoreResult }: { fetchMoreResult?: WorksMembershipsResponse }
      ): WorksMembershipsResponse {
        if (!fetchMoreResult) return previousQueryResult;

        return fetchMoreResult;
      },
    });
  }

  if (error) {
    return <ErrorHandler error={error} log="MembersList" />;
  }

  const addNewDropdown = [
    {
      label: (
        <Link
          to={`${
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          }/add`}
        >
          <Icon icon="user" kind="light" /> Create User
        </Link>
      ),
      key: 'single',
    },
    {
      label: (
        <Link
          to={`${
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          }/upload`}
        >
          <Icon icon="arrowUpFromBracket" kind="light" /> Upload Users
        </Link>
      ),
      key: 'multiple',
    },
  ];

  return (
    <>
      <section>
        <FilteredTable
          className="members-list"
          rowKey="id"
          columns={tableColumnsWithFilters(isOrganization)}
          filters={selectedFilters}
          dataSource={memberships}
          loading={loading}
          localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
          onChange={handleTableChange}
          pagination={{
            total: data?.worksMemberships?.metadata?.totalCount,
          }}
          onFilterChange={onFilterChange}
          onRow={(record: Membership) => ({
            onClick: record.organizationUnitsCount
              ? () => navigate(buildViewDrawerRoute(record.id))
              : null,
          })}
          rowClassName={(record: {
            id: string;
            organizationUnitsCount: number;
            active: boolean;
          }) => ({
            'highlight-recently-updated': updatedMemberIds.includes(record.id),
            'highlight-action-required':
              isOrganization && !record.organizationUnitsCount,
            'disabled-row': !record.active,
          })}
          headerRightSlot={
            <ButtonGroup size="large">
              {canUpdateAdminUsersUnits && (
                <Link
                  to={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`}
                >
                  <AddButton>Assign Locations</AddButton>
                </Link>
              )}
              <Dropdown
                overlayClassName="admin-members-page__add-new-dropdown"
                menu={{ items: addNewDropdown }}
                placement="bottomRight"
              >
                <AddButton showDropdown>Users</AddButton>
              </Dropdown>
            </ButtonGroup>
          }
        />
      </section>
      <style jsx>{`
        :global(.disabled-row) {
          background-color: ${Colors.steelLightest};
          color: ${Colors.midnightLighter}!important;
        }
        :global(.members-list .ant-table-tbody > tr > td) {
          padding: 20px 4px 20px 8px;
        }
        :global(td > a.members-list-row-link) {
          cursor: pointer;
          padding: 18px 12px 18px 0;
          font-weight: 800;
        }
        :global(.members-list .members-list__action-button) {
          margin-top: -6px;
          margin-bottom: -6px;
        }
        :global(.admin-members-page__add-new-dropdown a) {
          width: 100%;
        }
      `}</style>
    </>
  );
};
